/**
 * This is the file input component for the Vendor Application
 */
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { last } from 'lodash';
import { FormControl } from '@mui/material';
import styles from '../../VendorApplication/VendorApplication.module.css';
import stylesInputs from '../NewDesign/Inputs/Inputs.module.css';
import Button from '../Button/Button';
import { ReactComponent as TrashSVG } from '../../../Assets/icons/trash.svg';
import { ReactComponent as Download } from '../../../Assets/icons/download.svg';
import { ReactComponent as Upload } from '../../../Assets/icons/upload.svg';
import { Label } from '../NewDesign/Inputs/TextField';

function FileInputV2(props) {
  const {
    label,
    name,
    required = false,
    disabled = false,
    viewMode = false,
    error: errorProp = false,
    fileType,
    sizeLimit,
    isUploaded,
    onChange,
    getUploadUrl,
    getDownloadUrl,
  } = props;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleDownload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = await getDownloadUrl(name);
      setLoading(false);
      window.open(url, '_blank');
    } catch (err) {
      console.error(err);
      setLoading(false);
      setError(err.message);
    }
  }, [getDownloadUrl, name]);

  const handleUpload = useCallback(
    async (e) => {
      const {
        target: { files },
      } = e;
      const file = files[0];

      if (fileType !== file.type) {
        const errorMessage = `Only ${last(fileType.split('/'))} Files are Accepted`;
        setError(errorMessage);
        return;
      }
      if (file.size > sizeLimit * 1000000) {
        const errorMessage = `File upload cannot exceed ${sizeLimit}MB`;
        setError(errorMessage);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const url = await getUploadUrl(name);
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type,
          },
          body: file,
        });
        if (response.ok) {
          setLoading(false);
          onChange(name, true);
        } else {
          setLoading(false);
          setError('Upload Failed');
        }
      } catch (err) {
        console.error(err);
        setLoading(false);
        setError(err.message);
      }
    },
    [getUploadUrl, onChange, name, fileType, sizeLimit],
  );

  const handleRemove = useCallback(() => {
    setError(null);
    onChange(name, false);
  }, [onChange, name]);

  if (!viewMode) {
    return (
      <FormControl
        variant="standard"
        margin="dense"
        fullWidth
        required={required}
      >
        <Label label={label} required={required} error={errorProp} />

        <div className={styles.uploadButtons}>
          {isUploaded && (
            <Button
              variant="outlined"
              id="preview-button"
              color="primary"
              small
              icon
              flex
              disabled={loading || disabled}
              className={styles.documentButton}
              onClick={handleDownload}
            >
              <Download className={styles.iconPadLeft} />
              Download
            </Button>
          )}

          <label htmlFor={`va-${name}`}>
            <Button
              variant="contained"
              id="upload-button"
              component="span"
              color="primary"
              small
              flex
              icon
              disabled={loading || disabled}
              className={styles.documentButton}
            >
              <Upload className={styles.iconPadLeft} />
              {isUploaded ? 'Replace' : 'Upload'}
            </Button>
            <input
              accept={fileType}
              id={`va-${name}`}
              type="file"
              name={name}
              style={{ display: 'none' }}
              onChange={handleUpload}
              disabled={loading || disabled}
              value=""
            />
          </label>

          {isUploaded && (
            <Button
              variant="outlined"
              id="preview-button"
              color="primary"
              small
              icon
              flex
              disabled={loading || disabled}
              className={styles.documentButton}
              onClick={handleRemove}
            >
              <TrashSVG className={styles.iconPadLeft} />
              Delete
            </Button>
          )}
        </div>
        <br />
        <div className={styles.error}>{error}</div>
      </FormControl>
    );
  }
  return (
    <div className={stylesInputs.twoColumnLayout}>
      <div className={stylesInputs.labelColumn}>{label}</div>
      <div className={stylesInputs.valueColumn}>
        {isUploaded ? (
          <Button
            variant="outlined"
            id="preview-button"
            color="primary"
            small
            icon
            flex
            disabled={loading}
            className={styles.documentButton}
            onClick={handleDownload}
          >
            <Download className={styles.iconPadLeft} />
            Download
          </Button>
        ) : (
          <p>No file uploaded</p>
        )}
      </div>
    </div>
  );
}

FileInputV2.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  viewMode: PropTypes.bool,
  error: PropTypes.bool,

  fileType: PropTypes.string.isRequired,
  sizeLimit: PropTypes.number.isRequired,

  isUploaded: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,

  getUploadUrl: PropTypes.func.isRequired,
  getDownloadUrl: PropTypes.func.isRequired,
};

export default FileInputV2;
